import { ErrorCode } from '@openfeature/web-sdk';
import type {
  EvaluationContext,
  JsonValue,
  Logger,
  Provider,
  ProviderMetadata,
  ResolutionDetails,
} from '@openfeature/web-sdk';

import type {
  FlagOverride,
  OnError,
  OverrideProviderConfig,
  OverrideStorage,
} from '../types';

export class OverrideProvider implements Provider {
  public readonly metadata: ProviderMetadata = {
    name: 'OverrideProvider',
  };

  private readonly storage: OverrideStorage;
  private readonly storageKey: string;
  private overrides: Map<string, FlagOverride> = new Map();
  private shouldSkipOverrides: boolean = false;

  constructor(
    config: OverrideProviderConfig,
    private onError?: OnError,
  ) {
    this.storage = config.storage;
    this.storageKey = config.storageKey ?? 'feature-flag-overrides';
    this.loadOverrides();
  }

  public setSkipOverrides(skip: boolean): void {
    this.shouldSkipOverrides = skip;
  }

  private loadOverrides(): void {
    try {
      const stored = this.storage.getItem(this.storageKey);
      if (stored) {
        const overrides = JSON.parse(stored) as FlagOverride[];
        this.overrides = new Map(
          overrides
            .filter((override) => override.enabled)
            .map((override) => [override.key, override]),
        );
      }
    } catch (error) {
      this.onError?.(
        new Error(
          `[OverrideProvider] Failed to load overrides from ${this.storageKey}: ${error instanceof Error ? error.message : String(error)}`,
        ),
      );
    }
  }

  private saveOverrides(): void {
    try {
      const overrides = Array.from(this.overrides.values());
      this.storage.setItem(this.storageKey, JSON.stringify(overrides));
    } catch (error) {
      this.onError?.(
        new Error(
          `[OverrideProvider] Failed to save overrides to ${this.storageKey}: ${error instanceof Error ? error.message : String(error)}`,
        ),
      );
    }
  }

  private getOverride(key: string): FlagOverride | undefined {
    return this.overrides.get(key);
  }

  public setOverride(override: FlagOverride): void {
    this.overrides.set(override.key, override);
    this.saveOverrides();
  }

  public removeOverride(key: string): void {
    this.overrides.delete(key);
    this.saveOverrides();
  }

  public clearOverrides(): void {
    this.overrides.clear();
    this.storage.removeItem(this.storageKey);
  }

  public getAllOverrides(): FlagOverride[] {
    return Array.from(this.overrides.values());
  }

  resolveBooleanEvaluation(
    flagKey: string,
    defaultValue: boolean,
    _context: EvaluationContext,
    _logger: Logger,
  ): ResolutionDetails<boolean> {
    // Reload from storage to ensure we have the latest overrides
    this.loadOverrides();

    const override = this.getOverride(flagKey);

    if (!override || !override.enabled || this.shouldSkipOverrides) {
      return {
        value: defaultValue,
        errorCode: ErrorCode.FLAG_NOT_FOUND,
        reason: 'DEFAULT',
      };
    }

    if (override.type !== 'boolean') {
      return {
        value: defaultValue,
        errorCode: ErrorCode.TYPE_MISMATCH,
        reason: 'ERROR',
        errorMessage: `Flag ${flagKey} is not a boolean`,
      };
    }

    return {
      value: override.value as boolean,
      reason: 'STATIC',
      variant: String(override.value),
    };
  }

  resolveStringEvaluation(
    flagKey: string,
    defaultValue: string,
    _context: EvaluationContext,
    _logger: Logger,
  ): ResolutionDetails<string> {
    // Reload from storage to ensure we have the latest overrides
    this.loadOverrides();

    const override = this.getOverride(flagKey);

    if (!override || !override.enabled || this.shouldSkipOverrides) {
      return {
        value: defaultValue,
        errorCode: ErrorCode.FLAG_NOT_FOUND,
        reason: 'DEFAULT',
      };
    }

    if (override.type !== 'string') {
      return {
        value: defaultValue,
        errorCode: ErrorCode.TYPE_MISMATCH,
        reason: 'ERROR',
        errorMessage: `Flag ${flagKey} is not a string`,
      };
    }

    return {
      value: override.value as string,
      reason: 'STATIC',
      variant: override.value as string,
    };
  }

  resolveNumberEvaluation(
    flagKey: string,
    defaultValue: number,
    _context: EvaluationContext,
    _logger: Logger,
  ): ResolutionDetails<number> {
    // Reload from storage to ensure we have the latest overrides
    this.loadOverrides();

    const override = this.getOverride(flagKey);

    if (!override || !override.enabled || this.shouldSkipOverrides) {
      return {
        value: defaultValue,
        errorCode: ErrorCode.FLAG_NOT_FOUND,
        reason: 'DEFAULT',
      };
    }

    if (override.type !== 'number') {
      return {
        value: defaultValue,
        errorCode: ErrorCode.TYPE_MISMATCH,
        reason: 'ERROR',
        errorMessage: `Flag ${flagKey} is not a number`,
      };
    }

    return {
      value: override.value as number,
      reason: 'STATIC',
      variant: String(override.value),
    };
  }

  resolveObjectEvaluation<T extends JsonValue>(
    flagKey: string,
    defaultValue: T,
    _context: EvaluationContext,
    _logger: Logger,
  ): ResolutionDetails<T> {
    // Reload from storage to ensure we have the latest overrides
    this.loadOverrides();

    const override = this.getOverride(flagKey);

    if (!override || !override.enabled || this.shouldSkipOverrides) {
      return {
        value: defaultValue,
        errorCode: ErrorCode.FLAG_NOT_FOUND,
        reason: 'DEFAULT',
      };
    }

    if (override.type !== 'object') {
      return {
        value: defaultValue,
        errorCode: ErrorCode.TYPE_MISMATCH,
        reason: 'ERROR',
        errorMessage: `Flag ${flagKey} is not an object`,
      };
    }

    return {
      value: override.value as T,
      reason: 'STATIC',
    };
  }

  static isInstance(provider: Provider): provider is OverrideProvider {
    return provider.metadata.name === 'OverrideProvider';
  }
}
